import { cn, isFirefoxMobile } from "@fatbees/ui-utils";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cva, VariantProps } from "class-variance-authority";
import * as React from "react";
import {
  CSSProperties,
  ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useState,
} from "react";

const dialogContentVariants = cva(
  [
    "bg-background",
    "data-[state=open]:animate-in",
    "data-[state=closed]:animate-out",
    "data-[state=closed]:fade-out-0",
    "data-[state=open]:fade-in-0",
    "data-[state=closed]:zoom-out-95",
    "data-[state=open]:zoom-in-95",
    "fixed",
    "left-[50%]",
    "z-50",
    "w-full",
    "flex",
    "flex-col",
    "max-w-[calc(100%-2rem)]",
    "translate-x-[-50%]",
    "rounded-lg",
    "border",
    "p-4",
    "shadow-lg",
    "duration-200",
  ],
  {
    variants: {
      width: {
        sm: "sm:max-w-sm",
        md: "sm:max-w-lg",
        lg: "sm:max-w-2xl",
        full: "sm:max-w-[calc(100%-4rem)]",
      },
    },
    defaultVariants: {
      width: "md",
    },
  },
);

export interface DialogProps extends React.ComponentProps<
  typeof DialogPrimitive.Root
> {}

export function Dialog({ ...props }: DialogProps) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

export interface DialogTriggerProps extends React.ComponentProps<
  typeof DialogPrimitive.Trigger
> {}

export function DialogTrigger({ ...props }: DialogTriggerProps) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

export interface DialogPortalProps extends React.ComponentProps<
  typeof DialogPrimitive.Portal
> {}

export function DialogPortal({ ...props }: DialogPortalProps) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

export interface DialogCloseProps extends React.ComponentProps<
  typeof DialogPrimitive.Close
> {}

export function DialogClose({ ...props }: DialogCloseProps) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

export interface DialogOverlayProps extends React.ComponentProps<
  typeof DialogPrimitive.Overlay
> {}

export const DialogOverlay = forwardRef(function DialogOverlay(
  { className, ...props }: DialogOverlayProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50",
        className,
      )}
      ref={ref}
      {...props}
    />
  );
});

export interface DialogContentProps
  extends
    React.ComponentProps<typeof DialogPrimitive.Content>,
    VariantProps<typeof dialogContentVariants> {
  hasKeyboardInput?: boolean;
}

export function DialogContent({
  children,
  className,
  hasKeyboardInput,
  ...props
}: DialogContentProps) {
  const [style, setStyle] = useState<Partial<CSSProperties>>({});

  const updateWindowState = useCallback(() => {
    // Handle short screens and on-screen keyboards. We do it in code instead
    // of media queries because iOS doesn't always apply them on page load
    const isShortScreen = window.matchMedia("(max-height: 800px)").matches;
    const reserveOSKSpace =
      hasKeyboardInput &&
      window.matchMedia("(pointer: coarse)").matches &&
      !isFirefoxMobile();
    const oskHeight = 420;
    const windowHeight = window.innerHeight;

    if (!reserveOSKSpace) {
      setStyle({ top: "10vh", maxHeight: "80vh" });
    } else if (isShortScreen) {
      // iOS on an iPad mini uses the wrong value for vh, so we have to do
      // the calculation ourselves
      setStyle({
        top: "0",
        maxHeight: `${windowHeight - oskHeight}px`,
      });
    } else {
      setStyle({
        top: "10vh",
        maxHeight: `${windowHeight * 0.9 - oskHeight}px`,
      });
    }
  }, [hasKeyboardInput]);

  useEffect(() => {
    updateWindowState();

    window.addEventListener("resize", updateWindowState);

    return () => {
      window.removeEventListener("resize", updateWindowState);
    };
  }, [updateWindowState]);

  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(dialogContentVariants(props), style, className)}
        style={style}
        {...props}
      >
        {children}
        {/*<DialogPrimitive.Close className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4">*/}
        {/*  <XIcon />*/}
        {/*  <span className="sr-only">Close</span>*/}
        {/*</DialogPrimitive.Close>*/}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

export interface DialogHeaderProps extends React.ComponentProps<"div"> {}

export function DialogHeader({ className, ...props }: DialogHeaderProps) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex flex-col gap-6 text-center xs:text-left", className)}
      {...props}
    />
  );
}

export interface DialogFooterProps extends React.ComponentProps<"div"> {}

export function DialogFooter({ className, ...props }: DialogFooterProps) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn("mt-8 flex flex-row gap-2 justify-between", className)}
      {...props}
    />
  );
}

export interface DialogTitleProps extends React.ComponentProps<
  typeof DialogPrimitive.Title
> {}

export function DialogTitle({ className, ...props }: DialogTitleProps) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("text-lg leading-none font-semibold", className)}
      {...props}
    />
  );
}

export interface DialogDescriptionProps extends React.ComponentProps<
  typeof DialogPrimitive.Description
> {}

export function DialogDescription({
  className,
  ...props
}: DialogDescriptionProps) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}
